import { useCallback, useRef, useEffect, useState, useMemo } from 'react'; // Added useMemo
import { Business } from '@/schemas/business';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFirebase } from '@/context/FirebaseProvider'; // Import useFirebase

interface UseBusinessSelectorProps {
  businessName?: string; // This can be kept if there's a use case for pre-selecting by name
  onBusinessSelect?: (id: string) => void;
  hideAllBusinessesOption?: boolean;
  // allBusinesses prop is removed
}

export const useBusinessSelector = ({
  businessName,
  onBusinessSelect,
  hideAllBusinessesOption = false
}: UseBusinessSelectorProps) => {
  const { businesses: allBusinesses, isLoading: isLoadingBusinesses } = useFirebase(); // Get businesses from useFirebase
  const reactRouterNavigate = useNavigate();
  const location = useLocation();
  const [triggerWidth, setTriggerWidth] = useState<number>(0);
  const triggerRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  const currentBusiness = useMemo(() => {
    if (isLoadingBusinesses || !allBusinesses) {
      console.log("useBusinessSelector (memo) - Businesses loading or not available yet.");
      return undefined;
    }

    const pathParts = pathname.split('/');
    const businessIdFromUrl = pathParts[pathParts.indexOf('business') + 1];
    let foundBusiness: Business | undefined;

    if (businessIdFromUrl) {
      foundBusiness = allBusinesses.find(b => b.id === businessIdFromUrl);
      console.log("useBusinessSelector (memo) - Attempted find by URL ID:", businessIdFromUrl, "Found:", !!foundBusiness);
    }

    // Fallback to businessName prop if business not found by URL ID or no ID in URL
    if (!foundBusiness && businessName) {
      foundBusiness = allBusinesses.find(b => b.displayName === businessName || b.name === businessName);
      console.log("useBusinessSelector (memo) - Attempted find by businessName prop:", businessName, "Found:", !!foundBusiness);
    }
    
    console.log("useBusinessSelector (memo) - Final currentBusiness:", foundBusiness);
    return foundBusiness;
  }, [isLoadingBusinesses, allBusinesses, pathname, businessName]);
  
  // useEffect for logging currentBusiness when it changes
  useEffect(() => {
    console.log("useBusinessSelector (effect) - isLoadingBusinesses:", isLoadingBusinesses, "businessIdFromUrl:", pathname.split('/')[pathname.split('/').indexOf('business') + 1], "Final currentBusiness:", currentBusiness);
  }, [currentBusiness, isLoadingBusinesses, pathname]);


  // Update the trigger width when it changes
  useEffect(() => {
    if (triggerRef.current) {
      const updateWidth = () => {
        if (triggerRef.current) {
          setTriggerWidth(triggerRef.current.offsetWidth);
        }
      };
      
      // Initial measurement
      updateWidth();
      
      // Setup resize observer to update when trigger size changes
      const resizeObserver = new ResizeObserver(updateWidth);
      resizeObserver.observe(triggerRef.current);
      
      return () => {
        if (triggerRef.current) {
          resizeObserver.unobserve(triggerRef.current);
        }
      };
    }
  }, []);

  const handleNavigation = useCallback((business: Business, event: React.MouseEvent) => {
    // Prevent event propagation
    event.preventDefault();
    event.stopPropagation();
    
    console.log('BusinessSelector - Navigation triggered for:', business.id);
    
    // Force body click to close any open menus/dropdowns
    setTimeout(() => document.body.click(), 0);
    
    // Check if there's a custom business select callback
    if (onBusinessSelect) {
      console.log('Using custom onBusinessSelect callback');
      onBusinessSelect(business.id);
      return; // Exit early to prevent multiple navigation attempts
    }
    
    // Simply replace the business ID in the current URL path
    const currentPath = location.pathname;
    const currentSearch = location.search;
    
    // Check if we're on GoCampaign page
    const isGoCampaignPage = currentPath.includes('/gocampaign');
    
    if (currentPath.includes('/business/')) {
      // Keep the gocampaign part if it exists
      let newPath;
      if (isGoCampaignPage) {
        newPath = currentPath.replace(
          /\/business\/[^/]+/,
          `/business/${business.id}`
        );
      } else {
        newPath = `/business/${business.id}`;
      }
      
      console.log('Navigating to (by direct replacement):', newPath + currentSearch);
      
      // Use replace with a short delay
      setTimeout(() => {
        reactRouterNavigate(newPath + currentSearch, { replace: true });
      }, 50);
    } else {
      // Fallback to business dashboard
      console.log('Using fallback navigation to business dashboard');
      setTimeout(() => {
        reactRouterNavigate(`/business/${business.id}`);
      }, 50);
    }
  }, [onBusinessSelect, location.pathname, location.search, reactRouterNavigate]);

  const handleAllBusinessesNavigation = useCallback((event: React.MouseEvent) => {
    // Prevent event propagation
    event.preventDefault();
    event.stopPropagation();
    
    console.log('BusinessSelector - Navigating to all businesses');
    
    // Force body click to close any open menus/dropdowns
    setTimeout(() => document.body.click(), 0);
    
    // Short delay to ensure dropdown closes first
    setTimeout(() => {
      if (onBusinessSelect) {
        onBusinessSelect('home');
      } else {
        // Default: use React Router's navigate function
        reactRouterNavigate('/');
      }
    }, 50);
  }, [onBusinessSelect, reactRouterNavigate]);
  
  return {
    currentBusiness,
    triggerRef,
    triggerWidth,
    handleNavigation,
    handleAllBusinessesNavigation,
    hideAllBusinessesOption
  };
};
